/**
 * useDrawingTools Hook
 *
 * Provides access to drawing tools state and actions for drawing/editing ALL geometry types (Marker, Line, Shape).
 * Uses atomic selectors to minimize re-renders.
 *
 * Renamed from useMarkerTools to better reflect its responsibility:
 * - Manages ALL geometry types (not just markers)
 * - Handles drawing tool state (active tool, drawing mode, geometries)
 * - Provides unified interface for all drawing operations
 *
 * @example
 * ```typescript
 * const {
 *   activeTool,
 *   isDrawing,
 *   setActiveTool,
 *   updateGeometry
 * } = useDrawingTools();
 * ```
 */

import { useGeoMarkupStore } from '../store';

export const useDrawingTools = () => {
  // Atomic selectors - only re-render when specific values change
  const activeTool = useGeoMarkupStore((s) => s.activeTool);
  const isDrawing = useGeoMarkupStore((s) => s.isDrawing);
  const currentGeometries = useGeoMarkupStore((s) => s.currentGeometries);
  const activeGeometryId = useGeoMarkupStore((s) => s.activeGeometryId);
  const isPanelOpen = useGeoMarkupStore((s) => s.isPanelOpen);
  const activeSettingPanel = useGeoMarkupStore((s) => s.activeSettingPanel);
  const isCustomColorPickerOpen = useGeoMarkupStore((s) => s.isCustomColorPickerOpen);
  const activeColorTarget = useGeoMarkupStore((s) => s.activeColorTarget);
  const hideOtherMarkers = useGeoMarkupStore((s) => s.hideOtherMarkers);

  // Computed: active geometry from currentGeometries + activeGeometryId
  const activeGeometry = currentGeometries.find(g => g.id === activeGeometryId);

  // Actions (stable references - don't cause re-renders)
  const setActiveTool = useGeoMarkupStore((s) => s.setActiveTool);
  const addGeometry = useGeoMarkupStore((s) => s.addGeometry);
  const updateGeometry = useGeoMarkupStore((s) => s.updateGeometry);
  const removeGeometry = useGeoMarkupStore((s) => s.removeGeometry);
  const setActiveGeometry = useGeoMarkupStore((s) => s.setActiveGeometry);
  const clearGeometries = useGeoMarkupStore((s) => s.clearGeometries);
  const loadGeometries = useGeoMarkupStore((s) => s.loadGeometries);
  const togglePanel = useGeoMarkupStore((s) => s.togglePanel);
  const closePanel = useGeoMarkupStore((s) => s.closePanel);
  const setActiveSettingPanel = useGeoMarkupStore((s) => s.setActiveSettingPanel);
  const closeSettingPanel = useGeoMarkupStore((s) => s.closeSettingPanel);
  const openCustomColorPicker = useGeoMarkupStore((s) => s.openCustomColorPicker);
  const toggleCustomColorPicker = useGeoMarkupStore((s) => s.toggleCustomColorPicker);
  const closeCustomColorPicker = useGeoMarkupStore((s) => s.closeCustomColorPicker);
  const toggleHideOtherMarkers = useGeoMarkupStore((s) => s.toggleHideOtherMarkers);

  return {
    // State
    activeTool,
    isDrawing,
    currentGeometries,
    activeGeometryId,
    activeGeometry,
    isPanelOpen,
    activeSettingPanel,
    isCustomColorPickerOpen,
    activeColorTarget,
    hideOtherMarkers,

    // Actions
    setActiveTool,
    addGeometry,
    updateGeometry,
    removeGeometry,
    setActiveGeometry,
    clearGeometries,
    loadGeometries,
    togglePanel,
    closePanel,
    setActiveSettingPanel,
    closeSettingPanel,
    openCustomColorPicker,
    toggleCustomColorPicker,
    closeCustomColorPicker,
    toggleHideOtherMarkers,
  };
};
